import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSList,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BillingFeeItemType,
  Currency,
  Invoice,
  InvoiceStatus,
  LineItem,
  LineItemLineItemType,
  PayoutDestinationSummaryType,
  PayoutSummary,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useHistory } from "react-router-dom";
import { PAYOUTS_PATH } from "../../../modules/Payouts";
import { AlertEstimatedTaxWithholdings } from "../../../modules/TaxWithholdings/components/AlertEstimatedTaxWithholdings";
import { AlertTaxWithholdingsEnabled } from "../../../modules/TaxWithholdings/components/AlertTaxWithholdingsEnabled";
import { WSQueries } from "../../../query/WSQuery";
import {
  queriesDeductions,
  queriesInternalAccounts,
  queriesTaxWithholdings
} from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { selectorPayoutSettingsInstantFee } from "@selectors/selectorPayoutSettingsInstantFee";
import { instantPayoutService } from "../../../shared/services/instantPayout";
import { Totals, TotalsSection, TotalsSectionItem } from "../Totals/Totals";
import { getInvoiceTaxWithholdings } from "./getInvoiceTaxWithholdings";
import { getPayoutMethodName, usePayoutMethods } from "../../utils/payout";

const lineItemRegularPrice = (lineItem: LineItem) =>
  lineItem.unit
    ? (lineItem.unitCost || 0) * (lineItem.quantity || 0)
    : lineItem.totalCost || 0;

const lineItemDiscount = (lineItem: LineItem) => {
  if (lineItem.discount?.amount) {
    return lineItem.discount.amount;
  }

  if (lineItem.discount?.percentage) {
    return (
      (lineItemRegularPrice(lineItem) * lineItem.discount.percentage) / 100
    );
  }

  return 0;
};

const getLineItemsTotalsSection = (
  lineItems: LineItem[],
  currency: Currency,
  params?: { is1099Flow?: boolean }
): TotalsSection => {
  const items: TotalsSectionItem[] = [];

  lineItems.forEach(lineItem => {
    items.push([
      <>
        <WSFlexBox.CenterY wrap="nowrap">
          <WSText color="gray600">{lineItem.description}</WSText>
          {lineItem.lineItemType === LineItemLineItemType.REIMBURSEMENT ? (
            <WSPill py="NONE" ml="M" text="Reimbursable" />
          ) : null}
          {params?.is1099Flow && lineItem.metadata?.taxForm1099 ? (
            <WSPill theme="blue" ml="M" text="Included in 1099" />
          ) : null}
        </WSFlexBox.CenterY>

        {lineItem.unit && (
          <WSText.ParagraphSm color="gray500">
            {lineItem.quantity} {lineItem.unit}(s) @{" "}
            {formatMoney(lineItem.unitCost || 0, currency)}/{lineItem.unit}
          </WSText.ParagraphSm>
        )}
        {lineItem.detail && (
          <WSText.ParagraphSm color="gray500">
            {lineItem.detail}
          </WSText.ParagraphSm>
        )}
      </>,
      formatMoney(lineItemRegularPrice(lineItem), currency)
    ]);

    const discount = lineItemDiscount(lineItem);

    if (discount) {
      items.push([
        <WSText key="left" color="gray600">
          {lineItem.discount?.description}
          {lineItem.discount?.percentage
            ? ` (${lineItem.discount.percentage}%)`
            : ""}
        </WSText>,
        formatMoney(-discount, currency)
      ]);
    }
  });

  return { items };
};

const sumFees = (invoice: Invoice, type: BillingFeeItemType) =>
  (invoice.fees?.items || [])
    .filter(feeItem => feeItem.type === type)
    .reduce((total, feeItem) => total + feeItem.amount, 0);

const findFee = (invoice: Invoice, type: BillingFeeItemType) =>
  (invoice.fees?.items || []).find(feeItem => feeItem.type === type);

type Props = {
  invoice: Invoice;
  is1099Flow?: boolean;
} & WSElementProps;

export const InvoiceTotals: React.FC<Props> = ({
  invoice,
  is1099Flow,
  ...elementProps
}) => {
  const history = useHistory();
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const querySettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryDeductions = queriesDeductions.listDeductions.useQuery(
    { filter: { payeeId: { eq: invoice.payeeId } } },
    { enabled: (invoice.deductionIds || []).length > 0 }
  );

  const hasActiveTaxWithholdingAccount =
    queryInternalAccounts.data?.data?.some(
      selectorActiveTaxWithholdingAccount
    ) ?? false;

  const { payoutPreferences, payoutMethods } = usePayoutMethods();

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          queryPayoutSettings
        }}
      >
        {({ queryPayoutSettings: { data: payoutSettings } }) => {
          const currency = invoice.currency ?? Currency.USD;
          const instantPayoutDefaultFee =
            selectorPayoutSettingsInstantFee(payoutSettings);

          const totalElement =
            invoice.status !== InvoiceStatus.PAID &&
            invoice.status !== InvoiceStatus.PAYMENT_IN_TRANSIT ? (
              <WSFlexBox.CenterY>
                <WSText mr="M">Total</WSText>
                <WSPill icon="time" text="Pending" />
              </WSFlexBox.CenterY>
            ) : (
              "Total"
            );

          const totalsSections: TotalsSection[] = [];

          // Line items

          const lineItems = invoice.lineItems || [];
          const lateFee = sumFees(invoice, BillingFeeItemType.LATE);
          const lineItemsAmount = lineItems.reduce(
            (total, lineItem) =>
              total +
              lineItemRegularPrice(lineItem) -
              lineItemDiscount(lineItem),
            0
          );
          const lineItemsSum = lineItemsAmount + lateFee;

          const totalsLineItemsSection = getLineItemsTotalsSection(
            lineItems,
            currency,
            { is1099Flow }
          );

          if (lateFee > 0) {
            totalsLineItemsSection.items.push([
              "Late fee",
              formatMoney(lateFee, currency)
            ]);
          }

          totalsSections.push(totalsLineItemsSection);

          // Fees & collaborator payments

          const creditCardFee =
            invoice.creditFeeHandling?.payeeFeePercentage === 100
              ? sumFees(invoice, BillingFeeItemType.CARD)
              : 0;

          const instantPayoutFeeItem = findFee(
            invoice,
            BillingFeeItemType.INSTANT_PAYOUT
          );
          const instantPayoutFee = instantPayoutFeeItem?.amount
            ? instantPayoutFeeItem.amount
            : invoice.status !== InvoiceStatus.PAID &&
                payoutPreferences === PreferredPayoutSpeed.INSTANT
              ? instantPayoutService.calculateInvoiceInstantPayoutFee(
                  lineItemsSum,
                  payoutSettings
                )
              : 0;

          const splitPayments = (invoice.splits || []).reduce(
            (total, split) => total + split.amount,
            0
          );

          const grossIncome =
            lineItemsSum - creditCardFee - instantPayoutFee - splitPayments;

          if (creditCardFee > 0 || instantPayoutFee > 0 || splitPayments > 0) {
            const totalsFeesSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: totalElement,
                  right: formatMoney(lineItemsSum, currency)
                }
              ]
            };

            if (creditCardFee) {
              totalsFeesSection.items.push([
                "Credit card fee",
                formatMoney(-creditCardFee, currency)
              ]);
            }

            if (instantPayoutFee) {
              if (instantPayoutFeeItem?.amount) {
                totalsFeesSection.items.push([
                  `Instant payout fee (${
                    (instantPayoutFeeItem.percentage || 0) * 100
                  }%)`,
                  formatMoney(-instantPayoutFeeItem.amount, currency)
                ]);
              } else {
                totalsFeesSection.items.push([
                  `Instant payout fee (${instantPayoutDefaultFee}%)`,
                  formatMoney(-instantPayoutFee, currency)
                ]);
              }
            }

            if (splitPayments) {
              totalsFeesSection.items.push([
                "Payment to contractors",
                formatMoney(-splitPayments, currency)
              ]);
            }

            totalsSections.push(totalsFeesSection);
          }

          let netIncome = grossIncome;

          // Tax withholdings & deductions
          const { amount: taxWithholdingAmount, rate: taxWithholdingRate } =
            getInvoiceTaxWithholdings(
              querySettings.data,
              invoice,
              netIncome,
              hasActiveTaxWithholdingAccount
            );
          netIncome -= taxWithholdingAmount;

          const deductionIds = invoice.deductionIds || [];
          const deductions = (queryDeductions.data?.data || []).filter(
            deduction => deduction.id && deductionIds.includes(deduction.id)
          );
          deductions.forEach(deduction => {
            netIncome -= deduction.amount || 0;
          });

          if (deductions.length > 0 || taxWithholdingAmount > 0) {
            const section: TotalsSection = {
              items: [
                {
                  bold: true,
                  left:
                    grossIncome === lineItemsSum
                      ? totalElement
                      : "Gross income",
                  right: formatMoney(grossIncome, currency)
                },
                ...deductions.map(deduction => ({
                  left: deduction.name,
                  right: formatMoney(-(deduction.amount || 0), currency)
                }))
              ]
            };

            if (taxWithholdingAmount > 0) {
              section.items.push({
                left: `Tax withholdings (${taxWithholdingRate}%)`,
                right: formatMoney(-taxWithholdingAmount, currency)
              });
            }

            totalsSections.push(section);
          }

          if (invoice.events?.markedPaidAt) {
            // Recorded total

            totalsSections.push({
              items: [
                {
                  bold: true,
                  left: "Recorded total",
                  right: formatMoney(netIncome, currency)
                }
              ]
            });
          } else {
            // Payout destinations

            const payoutDestinationsSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: netIncome === grossIncome ? totalElement : "Net income",
                  right: formatMoney(netIncome, currency)
                }
              ]
            };

            const payouts = invoice.payouts || [];

            if (payouts.length > 0) {
              payouts
                .filter(payout => Boolean(payout.amount))
                .forEach(payout => {
                  payoutDestinationsSection.items.push(
                    payoutToTotals(payout, currency, history)
                  );
                });
            } else {
              if (payoutPreferences === PreferredPayoutSpeed.INSTANT) {
                payoutMethods.instant.active.forEach(payoutMethod => {
                  payoutDestinationsSection.items.push([
                    getPayoutMethodName(payoutMethod),
                    formatMoney(
                      (netIncome * (payoutMethod.percentage || 0)) / 100,
                      currency
                    )
                  ]);
                });
              } else {
                payoutMethods.standard.active.forEach(payoutMethod => {
                  payoutDestinationsSection.items.push([
                    getPayoutMethodName(payoutMethod),
                    formatMoney(
                      (netIncome * (payoutMethod.percentage || 0)) / 100,
                      currency
                    )
                  ]);
                });
              }
            }

            totalsSections.push(payoutDestinationsSection);
          }

          return (
            <WSList gap="XL">
              <Totals sections={totalsSections} />

              {!invoice.events?.markedPaidAt && (
                <>
                  <AlertEstimatedTaxWithholdings
                    invoiceNetAmount={netIncome}
                    invoiceCurrency={currency}
                  />
                  <AlertTaxWithholdingsEnabled />
                </>
              )}
            </WSList>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};

const payoutDestinationLabel = (payout: PayoutSummary): string => {
  const suffix = payout.destination?.last4
    ? ` (${payout.destination.last4})`
    : "";

  switch (payout.destination?.type) {
    case PayoutDestinationSummaryType.INTERNAL_ACCOUNT:
      return "Wingspan Wallet";
    case PayoutDestinationSummaryType.TAX_WITHHOLDING:
      return "Tax Withholding";
    case PayoutDestinationSummaryType.EXTERNAL_BANK_ACCOUNT:
      return `${payout.destination.brand || "Bank account"}${suffix}`;
    case PayoutDestinationSummaryType.PAYMENT_CARD:
      return `${payout.destination.brand || "Debit"}${suffix}`;
    case PayoutDestinationSummaryType.CHECK:
      return "Check";
    case PayoutDestinationSummaryType.GIFT_CARD:
      return "Gift card";
    default:
      return "";
  }
};

function payoutToTotals(
  payout: PayoutSummary,
  currency: Currency,
  history: ReturnType<typeof useHistory>
): TotalsSectionItem {
  return [
    <WSFlexBox key="left" alignItems="center" gap="M">
      <WSText color="gray500">{payoutDestinationLabel(payout)}</WSText>

      <WSButton.Link
        size="S"
        rightIcon="open-tab"
        onClick={() => {
          history.push(PAYOUTS_PATH + "/" + payout.id);
        }}
      >
        View
      </WSButton.Link>
    </WSFlexBox>,
    formatMoney(payout.amount, (payout.currency as Currency) || currency)
  ];
}
